import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { VIDEO_TYPE_DESC, VIDEO_TYPE_LABEL, type VideoType } from "@/types/video";
import {
  DEFAULT_FILTER,
  PAGE_SIZE,
  applyFilters,
  getAllVideos,
  getVideosByType,
  paginate,
  totalPages,
  type FilterState,
  type SortKey,
} from "@/utils/video";
import VideoCard from "@/components/base/VideoCard";
import FilterBar from "./components/FilterBar";
import Pagination from "./components/Pagination";
import NotFound from "@/pages/NotFound";

const VALID_TYPES: VideoType[] = ["movie", "tv", "variety"];

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const validType: VideoType = VALID_TYPES.includes(type as VideoType) ? (type as VideoType) : "movie";

  const filters: FilterState = {
    region: searchParams.get("region") || DEFAULT_FILTER.region,
    year: searchParams.get("year") || DEFAULT_FILTER.year,
    genre: searchParams.get("genre") || DEFAULT_FILTER.genre,
    sort: (searchParams.get("sort") as SortKey) || DEFAULT_FILTER.sort,
    keyword: searchParams.get("keyword") || "",
  };
  const pageParam = Number(searchParams.get("page")) || 1;
  const scope = searchParams.get("scope") || "";
  const keyword = filters.keyword;
  const isSearch = scope === "all" || keyword.length > 0;

  const baseList = scope === "all" ? getAllVideos() : getVideosByType(validType);
  const filtered = applyFilters(baseList, filters);
  const pages = totalPages(filtered.length, PAGE_SIZE);
  const safePage = Math.min(Math.max(1, pageParam), pages);
  const current = paginate(filtered, safePage, PAGE_SIZE);

  const heading = isSearch
    ? keyword
      ? `“${keyword}” 的搜索结果`
      : "全部影视"
    : VIDEO_TYPE_LABEL[validType];
  const subHeading = isSearch
    ? `共找到 ${filtered.length} 部相关影视内容`
    : VIDEO_TYPE_DESC[validType];

  const seoTitle = isSearch
    ? `${heading} - 麻豆免费视频免费在线观看`
    : `${VIDEO_TYPE_LABEL[validType]} - 麻豆免费视频免费在线观看高清${VIDEO_TYPE_LABEL[validType]}`;
  const seoDescription = isSearch
    ? `麻豆免费视频为你提供${heading}，收录大量免费高清影视资源，支持分类筛选与分页浏览。`
    : `${VIDEO_TYPE_DESC[validType]} 麻豆免费视频提供${VIDEO_TYPE_LABEL[validType]}分类筛选、排序与分页，免费在线观看高清内容。`;

  useDocumentMeta({
    title: seoTitle,
    description: seoDescription,
    keywords: `麻豆免费视频,${VIDEO_TYPE_LABEL[validType]},免费在线观看,高清${VIDEO_TYPE_LABEL[validType]},${keyword || "影视视频"}`,
    canonicalPath: isSearch ? `/category/${validType}` : `/category/${validType}`,
  });

  if (!VALID_TYPES.includes(type as VideoType)) {
    return <NotFound />;
  }

  const updateFilter = (key: keyof FilterState, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "全部") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete("page");
    setSearchParams(next);
  };

  const updateSort = (sort: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (sort === DEFAULT_FILTER.sort) {
      next.delete("sort");
    } else {
      next.set("sort", sort);
    }
    next.delete("page");
    setSearchParams(next);
  };

  const handleReset = () => {
    const next = new URLSearchParams();
    if (scope) next.set("scope", scope);
    if (keyword) next.set("keyword", keyword);
    setSearchParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) {
      next.delete("page");
    } else {
      next.set("page", String(p));
    }
    setSearchParams(next);
  };

  const hasActiveFilter =
    filters.region !== "全部" || filters.year !== "全部" || filters.genre !== "全部" || filters.sort !== "updated";

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10 lg:px-10">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500 md:text-sm">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-200">{heading}</span>
      </nav>

      <div className="mt-5 rounded-xl border border-background-800 bg-gradient-to-r from-background-900 to-primary-950/30 p-6 md:p-8">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-foreground-50 md:text-3xl">{heading}</h1>
          <span className="rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium text-primary-400">
            {filtered.length} 部内容
          </span>
        </div>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">{subHeading}</p>
      </div>

      <div className="mt-6">
        <FilterBar type={validType} filters={filters} onChange={updateFilter} onSort={updateSort} onReset={handleReset} />
      </div>

      {current.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-6">
            {current.map((video, i) => (
              <VideoCard key={video.id} video={video} index={i} showType={isSearch} />
            ))}
          </div>
          <Pagination page={safePage} totalPages={pages} onChange={goPage} />
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-700 bg-background-900/60 px-6 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
            <i className="ri-search-eye-line text-3xl"></i>
          </span>
          <h3 className="mt-5 text-lg font-semibold text-foreground-100">没有找到匹配的影视内容</h3>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            换个关键词或调整筛选条件试试，也可以直接重置所有筛选查看全部内容。
          </p>
          <button
            type="button"
            onClick={handleReset}
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      )}

      {hasActiveFilter && current.length > 0 && (
        <p className="mt-6 text-center text-xs text-foreground-600">
          当前筛选：{filters.region} · {filters.year} · {filters.genre}
        </p>
      )}
    </div>
  );
}